import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Phone, Users, Video, ChevronDown, ChevronUp, PhoneCall, Loader2, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

const TYPE_ICON = { phone: Phone, in_person: Users, video: Video };
const TYPE_LABEL = { phone: "Phone", in_person: "In person", video: "Video" };

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" }) +
    ", " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

function CallItem({ c }) {
  const [open, setOpen] = useState(false);
  const Icon = TYPE_ICON[c.call_type] || Phone;
  const hasMore = !!(c.minutes || (c.action_points || []).length);
  return (
    <div className="border border-slate-100 rounded-lg p-3" data-testid={`call-history-item-${c.id}`}>
      <button onClick={() => hasMore && setOpen(!open)} className={`w-full text-left ${hasMore ? "cursor-pointer" : "cursor-default"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
            <Icon className="h-3 w-3" /> {TYPE_LABEL[c.call_type] || "Call"}
          </span>
          <span className="text-xs font-semibold text-slate-700">{c.user_name}</span>
          {c.contact_name && <span className="text-xs text-slate-500">→ {c.contact_name}{c.contact_phone ? ` (${c.contact_phone})` : ""}</span>}
          <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">{fmtWhen(c.call_at)}</span>
          {hasMore && (open ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />)}
        </div>
        {c.summary && <p className="text-sm text-slate-700 mt-1.5">{c.summary}</p>}
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
          {c.minutes && <p className="text-xs text-slate-600 whitespace-pre-wrap">{c.minutes}</p>}
          {(c.action_points || []).length > 0 && (
            <ul className="space-y-1">
              {c.action_points.map((ap, i) => (
                <li key={ap.id || i} className="flex items-start gap-1.5 text-xs text-slate-600">
                  {ap.done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" /> : <Circle className="h-3.5 w-3.5 text-slate-300 mt-0.5 shrink-0" />}
                  <span className={ap.done ? "line-through text-slate-400" : ""}>{ap.text}{ap.due_date ? ` · due ${ap.due_date}` : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function CallHistoryList({ linkedType, linkedId, company }) {
  const { data, isLoading } = useQuery({
    queryKey: ["call-history", linkedType, linkedId, company],
    queryFn: async () => (await api.get("/calls/for-record", { params: { linked_type: linkedType, linked_id: linkedId || "", company: company || "" } })).data,
    enabled: !!(linkedId || company),
  });
  if (isLoading) return <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>;
  if (!data?.length) return <p className="text-sm text-slate-400 text-center py-4" data-testid="call-history-empty">No calls logged for this record yet.</p>;
  return (
    <div className="space-y-2" data-testid="call-history-list">
      {data.map((c) => <CallItem key={c.id} c={c} />)}
    </div>
  );
}

export function CallHistoryCard({ linkedType, linkedId, company }) {
  return (
    <Card className="p-5" data-testid="call-history-card">
      <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2">
        <PhoneCall className="h-4 w-4 text-slate-400" /> Call History
      </h3>
      <CallHistoryList linkedType={linkedType} linkedId={linkedId} company={company} />
    </Card>
  );
}
